"use client";
import React from "react";

import { Box, BoxSx, ThemeColors } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useWarning } from "../../hooks/useWarning";

type BadgeType = "critical" | "warning" | "success" | "info" | "neutral";

export interface BadgeProps {
  numberLabel?: number;
  variant?: BadgeType;
}

const BadgeColor: Record<BadgeType, ThemeColors> = {
  critical: "badge-surface-color-critical",
  warning: "badge-surface-color-warning",
  success: "badge-surface-color-success",
  info: "badge-surface-color-info",
  neutral: "badge-surface-color-neutral"
};

export const Badge: React.FC<BadgeProps> = (props: BadgeProps) => {
  const { numberLabel, variant = "neutral" } = props;
  const { warn } = useWarning();

  if (numberLabel !== undefined && numberLabel < 0) {
    warn("numberLabel is restricted to positive numeric values");
  }

  const label = React.useMemo(() => {
    if (numberLabel !== undefined && numberLabel >= 0) {
      if (numberLabel > 999) {
        warn(
          "numberLabel values that exceed 3 digits will be truncated with a + sign"
        );
        return "999+";
      }
      return numberLabel.toString();
    }
    return undefined;
  }, [numberLabel, warn]);

  const containerStyles = React.useMemo<BoxSx>(() => {
    return {
      display: label ? "inline-flex" : "flex",
      flexShrink: 0,
      alignItems: "center",
      paddingTop: "spacing-1",
      paddingBottom: "spacing-1",
      paddingLeft: "spacing-2",
      paddingRight: "spacing-2",
      borderRadius: "radius-2",
      height: label ? "spacing-5" : "spacing-3",
      width: label ? "max-content" : "spacing-3",
      background: BadgeColor[variant]
    };
  }, [label, variant]);

  return (
    <Box sx={{ ...containerStyles }}>
      {label && (
        <Text
          size="font-size-1"
          weight="medium"
          letterSpacing="kerning-2"
          sx={{ color: "badge-onSurface" }}
        >
          {label}
        </Text>
      )}
    </Box>
  );
};
